// src/pages/FundPlanner/reports/exportPortfolioMainPdf.js

import dayjs from "lib/dayjs";
import { createCompassPdf } from "reporting/pdf/createCompassPdf";
import { addKpiCards } from "reporting/pdf/addKpiCards";
import { addReportTable } from "reporting/pdf/addReportTable";
import {
  PDF_CONTENT_GAP,
  addKeyValueBlock,
  addSectionTitle,
  ensurePageSpace,
} from "reporting/pdf/pdfLayout";
import {
  fmtCurrency,
  fmtDate,
  fmtNumber,
  fmtPctDecimal,
  safeFilePart,
} from "reporting/pdf/pdfFormatters";
import { buildPortfolioMainReportData } from "pages/FundPlanner/components/Portfolios/Main/portfolioMainReportData";
import { PDF_STONE } from "reporting/pdf/pdfTheme";
import { renderFundOverviewPdf } from "./renderFundOverviewPdf";
import {
  getPortfolioPeriodOpeningDate,
  getPortfolioPeriodPdfLabel,
} from "./portfolioPeriodPdfLabels";

function fmtYears(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "\u2014";
  return `${fmtNumber(n, 2)} yrs`;
}

const OVERVIEW_KPIS = [
  ["Par/Face Amount", "totalPar", (value) => fmtCurrency(value, 2)],
  ["Market Value", "totalMarketValue", (value) => fmtCurrency(value, 2)],
  ["Book Value", "totalBookValue", (value) => fmtCurrency(value, 2)],
  ["Accrued Interest", "totalAccruedInterest", (value) => fmtCurrency(value, 2)],
  ["MV + Accrued", "totalMarketValuePlusAccrued", (value) => fmtCurrency(value, 2)],
  ["Original Cost", "totalOriginalCost", (value) => fmtCurrency(value, 2)],
  ["Unrealized Gain/Loss", "totalUnrealizedGainLoss", (value) => fmtCurrency(value, 2)],
  ["Effective Yield", "avgYieldToWorst", (value) => fmtPctDecimal(value, 2)],
  ["WAM (Years)", "wamYears", fmtYears],
];

function getOverviewKpis(reportMode) {
  if (reportMode === "period") return OVERVIEW_KPIS;
  return OVERVIEW_KPIS.filter(([label]) => label !== "Avg Yield to Worst");
}

export function buildPeriodPortfolioColumns(periodOverview = {}) {
  const openingDate = getPortfolioPeriodOpeningDate(periodOverview.startDate);
  return [
    { key: "label", label: "Metric", align: "left" },
    {
      key: "beginning",
      label: `As of ${fmtDate(openingDate)} EOD`,
      align: "right",
      format: formatPeriodPortfolioValue,
    },
    {
      key: "ending",
      label: `As of ${fmtDate(periodOverview.endDate)} EOD`,
      align: "right",
      format: formatPeriodPortfolioValue,
    },
    { key: "change", label: "Change", align: "right", format: formatPeriodPortfolioValue },
  ];
}

const PERIOD_ACTIVITY_COLUMNS = [
  { key: "label", label: "Metric", align: "left" },
  { key: "value", label: "Amount", align: "right", format: (value) => fmtCurrency(value, 2) },
];

const ALLOCATION_COLUMNS = [
  { key: "label", label: "Category", align: "left" },
  { key: "marketValue", label: "Market Value", align: "right", format: (value) => fmtCurrency(value, 2) },
  { key: "percentOfTotal", label: "% Total", align: "right", format: (value) => fmtPctDecimal(value, 1) },
];

const CREDIT_RATING_COLUMNS = [
  { key: "label", label: "Rating", align: "left" },
  { key: "marketValue", label: "Market Value", align: "right", format: (value) => fmtCurrency(value, 2) },
  { key: "percentOfTotal", label: "Portfolio Allocation", align: "right", format: (value) => fmtPctDecimal(value, 1) },
];

const MATURITY_COLUMNS = [
  { key: "label", label: "Tenor", align: "left" },
  { key: "marketValue", label: "Market Value", align: "right", format: (value) => fmtCurrency(value, 2) },
  { key: "percentOfTotal", label: "% Total", align: "right", format: (value) => fmtPctDecimal(value, 1) },
];

const HOLDING_COLUMNS = [
  { key: "investment_type", label: "Type", align: "left" },
  { key: "cusip_or_id", label: "CUSIP / ID", align: "left" },
  { key: "issuer_name", label: "Issuer", align: "left" },
  { key: "market_value", label: "Market Value", align: "right", format: (value) => fmtCurrency(value, 2) },
  { key: "percentOfTotal", label: "% Total", align: "right", format: (value) => fmtPctDecimal(value, 2) },
  { key: "par_amount", label: "Par / Face Amt", align: "right", format: (value) => fmtNumber(value, 2) },
  { key: "effective_maturity_date", label: "Eff. Maturity", align: "left", format: (value) => (value ? fmtDate(value) : "") },
  { key: "rating_rollup", label: "Rating", align: "left" },
];

const TOP_HOLDING_COLUMN_WIDTHS = [56, 52, 105, 62, 40, 64, 56, 38];

function formatPeriodPortfolioValue(value, row) {
  if (row?.format === "percent") return fmtPctDecimal(value, 2);
  if (row?.format === "years") return fmtYears(value);
  return fmtCurrency(value, 2);
}

function getTopHoldingColumnStyles(usableWidth) {
  const baseWidth = TOP_HOLDING_COLUMN_WIDTHS.reduce((sum, width) => sum + width, 0);
  const scale = usableWidth / baseWidth;

  return TOP_HOLDING_COLUMN_WIDTHS.reduce((styles, width, index) => {
    styles[index] = { cellWidth: width * scale };
    return styles;
  }, {});
}

function addBarList(ctx, title, rows = [], startY) {
  const { doc, margins, usableWidth } = ctx;
  const safeRows = Array.isArray(rows) ? rows : [];
  const rowHeight = 18;
  const rowPaddingTop = 8;
  const neededHeight = 24 + rowPaddingTop + safeRows.length * rowHeight + 10;
  let y = ensurePageSpace(ctx, startY, neededHeight);

  y = addSectionTitle(ctx, title, y);

  if (!safeRows.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text("No data available.", margins.left, y);
    return y + PDF_CONTENT_GAP;
  }

  const labelWidth = 92;
  const percentWidth = 48;
  const barX = margins.left + labelWidth;
  const barWidth = usableWidth - labelWidth - percentWidth - 12;
  const barHeight = 7;
  let rowY = y + rowPaddingTop;

  safeRows.forEach((row) => {
    const pct = Math.max(0, Math.min(1, Number(row.percentOfTotal || 0)));
    const barY = rowY - 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(35);
    doc.text(String(row.label || ""), margins.left, rowY, {
      maxWidth: labelWidth - 8,
    });

    doc.setFillColor(...PDF_STONE.surface);
    doc.roundedRect(barX, barY, barWidth, barHeight, 3, 3, "F");

    if (pct > 0) {
      doc.setFillColor(...PDF_STONE.primary);
      doc.roundedRect(barX, barY, barWidth * pct, barHeight, 3, 3, "F");
    }

    doc.setTextColor(90);
    doc.text(fmtPctDecimal(pct, 1), margins.left + usableWidth, rowY, {
      align: "right",
    });

    rowY += rowHeight;
  });

  return rowY + PDF_CONTENT_GAP;
}

function addOverview(ctx, metrics, startY, reportMode) {
  const kpis = getOverviewKpis(reportMode).map(([label, key, format]) => ({
    label,
    value: format(metrics?.[key]),
  }));

  let y = addSectionTitle(ctx, "Overview", startY, {
    spaceBefore: 8,
    spaceAfter: 8,
  });
  return addKpiCards(ctx, kpis, y);
}

function addPeriodOverview(ctx, periodOverview, startY) {
  let y = addReportTable(
    ctx,
    {
      title: "Portfolio Summary",
      columns: buildPeriodPortfolioColumns(periodOverview),
      rows: periodOverview?.portfolioRows || [],
      fontSize: 7.5,
      headStyles: {
        fillColor: PDF_STONE.primary,
        textColor: [255, 255, 255],
      },
    },
    startY
  );

  return addReportTable(
    ctx,
    {
      title: "Income and Activity Summary",
      columns: PERIOD_ACTIVITY_COLUMNS,
      rows: periodOverview?.activityRows || [],
      fontSize: 7.5,
      headStyles: {
        fillColor: PDF_STONE.primary,
        textColor: [255, 255, 255],
      },
    },
    y
  );
}

function addAllocationSection(ctx, data) {
  ctx.doc.addPage();
  let y = ctx.margins.top;
  y = addBarList(ctx, "Allocation by Investment Type", data.allocationByType.items, y);
  const allocationRows = [
    ...data.allocationByType.items,
    {
      label: "Portfolio Total",
      marketValue: data.allocationByType.totalMarketValue,
      percentOfTotal: data.allocationByType.totalMarketValue ? 1 : 0,
      rowKind: "portfolio_total",
    },
  ];
  y = addReportTable(
    ctx,
    {
      columns: ALLOCATION_COLUMNS,
      rows: allocationRows,
      fontSize: 7.3,
      didParseCell: (hookData) => {
        if (hookData.section !== "body") return;
        const row = allocationRows[hookData.row.index];
        if (row?.rowKind !== "portfolio_total") return;
        hookData.cell.styles.fontStyle = "bold";
        hookData.cell.styles.fillColor = PDF_STONE.surface;
        hookData.cell.styles.lineColor = PDF_STONE.secondaryLight;
        hookData.cell.styles.lineWidth = {
          top: 0.8,
          right: 0.25,
          bottom: 0.25,
          left: 0.25,
        };
      },
    },
    y
  );

  ctx.doc.addPage();
  y = ctx.margins.top;
  y = addBarList(
    ctx,
    "Allocation by Rating",
    data.creditRatingSummary?.combinedAllocation || [],
    y
  );

  const ratingRows = [];
  for (const section of data.creditRatingSummary?.sections || []) {
    ratingRows.push({ label: section.label, rowKind: "section" });
    ratingRows.push(...section.items);
    ratingRows.push({
      label: `Total ${section.label}`,
      marketValue: section.totalMarketValue,
      percentOfTotal: section.percentOfTotal,
      rowKind: "subtotal",
    });
  }
  ratingRows.push({
    label: "Portfolio Total",
    marketValue: data.creditRatingSummary?.portfolioMarketValue || 0,
    percentOfTotal: 1,
    rowKind: "portfolio_total",
  });

  return addReportTable(
    ctx,
    {
      columns: CREDIT_RATING_COLUMNS,
      rows: ratingRows,
      fontSize: 7.3,
      didParseCell: (hookData) => {
        if (hookData.section !== "body") return;
        const row = ratingRows[hookData.row.index];
        if (row?.rowKind === "section") {
          hookData.cell.styles.fontStyle = "bold";
          hookData.cell.styles.fillColor = PDF_STONE.surface;
          if (hookData.column.index > 0) hookData.cell.text = [];
        } else if (row?.rowKind === "subtotal") {
          hookData.cell.styles.fontStyle = "bold";
        } else if (row?.rowKind === "portfolio_total") {
          hookData.cell.styles.fontStyle = "bold";
          hookData.cell.styles.fillColor = PDF_STONE.surface;
          hookData.cell.styles.lineColor = PDF_STONE.secondaryLight;
          hookData.cell.styles.lineWidth = {
            top: 0.8,
            right: 0.25,
            bottom: 0.25,
            left: 0.25,
          };
        }
      },
    },
    y
  );
}

function addMaturitySection(ctx, data) {
  ctx.doc.addPage();
  let y = addBarList(
    ctx,
    "Maturity Distribution",
    data.maturityData.buckets,
    ctx.margins.top
  );
  return addReportTable(
    ctx,
    {
      columns: MATURITY_COLUMNS,
      rows: data.maturityData.buckets,
      fontSize: 7.3,
    },
    y
  );
}

function addTopHoldingsSection(ctx, data) {
  ctx.doc.addPage();
  return addReportTable(
    ctx,
    {
      title: "Top Holdings",
      columns: HOLDING_COLUMNS,
      rows: data.topHoldings,
      fontSize: 6.2,
      columnStyles: getTopHoldingColumnStyles(ctx.usableWidth),
    },
    ctx.margins.top
  );
}

export function renderPortfolioMainPdf(ctx, data) {
  const isPeriodReport = data.meta.reportMode === "period" && data.periodOverview;
  const { margins } = ctx;
  // Pull the first metadata row closer to the fixed header's as-of/period line.
  let y = margins.top - 4;

  y = addKeyValueBlock(
    ctx,
    [
      { label: "Portfolio", value: data.meta.portfolioName || "Current Portfolio" },
    ],
    y,
    {
      columns: 1,
      labelWidth: 58,
      rowHeight: 14,
      spaceAfter: 2,
    }
  );

  y = addOverview(ctx, data.overviewMetrics, y, data.meta.reportMode);

  if (isPeriodReport) {
    addPeriodOverview(ctx, data.periodOverview, y);
  }

  addAllocationSection(ctx, data);
  addMaturitySection(ctx, data);
  addTopHoldingsSection(ctx, data);

  if (data.fundOverview) {
    const asOfLabel = data.meta.reportMode === "period"
      ? getPortfolioPeriodPdfLabel(data.meta.startDate, data.meta.endDate)
      : `As of ${fmtDate(data.meta.asOfDate)}`;
    ctx.startSection({
      reportTitle: "FundPlanner - Fund Overview",
      orgName: data.meta.orgName || data.meta.portfolioName,
      asOfLabel,
      orientation: data.meta.reportMode === "period" ? "landscape" : "portrait",
      contentMargins: { top: 102, bottom: 58 },
    });
    renderFundOverviewPdf(ctx, data.fundOverview, data.meta.portfolioName);
  }
}

export async function exportPortfolioMainPdf({
  reportData = null,
  save = true,
  ...builderArgs
} = {}) {
  const data = reportData || buildPortfolioMainReportData(builderArgs);
  const isPeriodReport = data.meta.reportMode === "period" && data.periodOverview;
  const asOfLabel = isPeriodReport
    ? getPortfolioPeriodPdfLabel(data.meta.startDate, data.meta.endDate)
    : data.meta.asOfDate
    ? `As of ${fmtDate(data.meta.asOfDate)}`
    : "";

  const ctx = await createCompassPdf({
    orgId: builderArgs.orgId,
    reportTitle: data.meta.reportTitle,
    orgName: data.meta.orgName || data.meta.portfolioName,
    asOfLabel,
    createdStamp: new Date(),
    orientation: "portrait",
    branding: "unbranded",
    contentMargins: {
      top: 102,
    },
  });

  renderPortfolioMainPdf(ctx, data);

  ctx.finalize();

  const { doc } = ctx;

  const fileName = `FundPlanner_Overview_${safeFilePart(
    data.meta.orgName || data.meta.portfolioName || "Portfolio"
  )}_${safeFilePart(data.meta.portfolioName || "Overview")}_${dayjs(
    data.meta.fileDate
  ).format("YYYY-MM-DD")}.pdf`;

  if (save) {
    doc.save(fileName);
  }

  return {
    doc,
    fileName,
    reportData: data,
  };
}

export default exportPortfolioMainPdf;
